import React, { Fragment, useContext, useEffect, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, GridLegacy as Grid } from "@mui/material";
import { api, Category, Distribution, IDistributionList } from "../../../../../logic/api";
import propTypes from "prop-types";
import EditableGroup, { EditableGroupType } from "../../../EditableGroup/EditableGroup";
import { SessionContext } from "../../../../../utility/useSession";
import EditableField, { EditableFieldTypes } from "../../EditableField";
import ConfirmationDialog from "../../../../notifications/confirmationDialog/ConfirmationDialog";
import DistributionListItem from "../../../../lists/listItems/DistributionListItem/DistributionListItem";
import { PagedDataView } from "../../../../lists/PagedDataView/PagedDataView";
import ErrorDialog from "../../../../notifications/errorDialog/ErrorDialog";
import { useTranslation } from "react-i18next";

const RecipientDialog: React.FunctionComponent<{

    show: boolean;
    category: Category;
    selected?: Distribution | null;
    onClose: (didUpdate: boolean) => void;
    onExistingRecipientSelected: (existing: Distribution) => void;
}> = (props) => {

    const defaultRecpt = (props.selected == null ? {
        category: props.category,
        email: "",
        name: "",
        mobile: ""
    } as Distribution : props.selected) as Distribution;

    const [recipient, setRecipient] = useState<Distribution>(defaultRecpt);
    const { t } = useTranslation();

    useEffect(() => {
        setRecipient(defaultRecpt);
        setShowDeleteConfirmation(false);
    }, [props.selected, props.category]);

    const session = useContext(SessionContext);
    const isNew = recipient.id == null;

    const createOrUpdate = api.hooks.distribution.createOrUpdate();
    const deleteRecpt = api.hooks.distribution.delete();
    const removeRecpt = api.hooks.distribution.unlink();
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);


    const defaultListParams = { filterEmail: "", filterName: "", excludeCategoryId: props.category.id, };

    const [distCatParams, setDistCatParams] = useState<IDistributionList>(defaultListParams);
    const [timeoutCancel, setTimeoutCancel] = useState<NodeJS.Timeout | null>(null);

    useEffect(() => {
        setDistCatParams(defaultListParams);
        setRecipient(defaultRecpt);
    }, [props.category]);

    const save = () => {
        createOrUpdate.send(session.current.context, {
            distribution: recipient,
            category: props.category,
        }, (result) => {
            if (result != null) {
                setRecipient(defaultRecpt);
                props.onClose(true);
            }
        });
    }
    const removeRecipient = (confirmed: boolean) => {
        if (confirmed == false) {
            setShowRemoveConfirmation(true);
            return;
        }
        if (recipient == null) return;
        removeRecpt.send(session.current.context, {
            distributionId: recipient.id == null ? 0 : recipient.id,
            categoryId: props.category.id,
        }, (result) => {
            if (result != null) {
                setRecipient(defaultRecpt);
                props.onClose(true);
            }
        });
    }

    const deleteRecipient = (confirmed: boolean) => {
        if (confirmed == false) {
            setShowDeleteConfirmation(true);
            return;
        }
        if (recipient == null) return;
        deleteRecpt.send(session.current.context, {
            distributionId: recipient.id == null ? 0 : recipient.id
        }, (result) => {
            if (result != null) {
                setRecipient(defaultRecpt);
                props.onClose(true);
            }


        });
    }

    return <Fragment>

        <Dialog maxWidth="sm" fullWidth={true} open={props.show}>
            <DialogTitle>{isNew ? t("editable.createRecipient") : t("editable.editRecipient")}</DialogTitle>
            <DialogContent>
                <ErrorDialog err={createOrUpdate.err} />
                <ConfirmationDialog title={t("editable.deleteRecipient")} message={t("editable.deleteRecipientConfirm")} show={showDeleteConfirmation} onClose={(confirmed) => {
                    setShowDeleteConfirmation(false);
                    if (confirmed == true) {
                        deleteRecipient(confirmed);
                    }

                }}></ConfirmationDialog>
                <ConfirmationDialog title={t("editable.removeRecipient", { name: props.category.categoryName.toUpperCase() })} message={t("editable.removeRecipientConfirm", { name: props.category.categoryName.toUpperCase() })} show={showRemoveConfirmation} onClose={(confirmed) => {
                    setShowRemoveConfirmation(false);
                    if (confirmed == true) {
                        removeRecipient(confirmed);
                    }

                }}></ConfirmationDialog>
                <EditableGroup<Distribution>
                    id="recipientSettings"
                    testId="recipient-settings-group"
                    editType={EditableGroupType.alwaysEditing}
                    data={recipient}
                    onDataChanged={async (data) => {
                        setRecipient(data);
                        return true;
                    }}
                >
                    <Grid container>

                        <EditableField<Distribution, string>
                            inputType={EditableFieldTypes.text}
                            label={t("editable.fullName")}
                            onProvide={(data) => data.name}
                            onChange={(data, value) => {
                                data.name = value;
                                if (timeoutCancel != null) {
                                    clearTimeout(timeoutCancel);
                                }
                                setTimeoutCancel(setTimeout(() => {
                                    setDistCatParams({ ...distCatParams, filterName: data.name, });
                                }, 1000));
                            }}
                        />
                        <EditableField<Distribution, string>
                            inputType={EditableFieldTypes.text}
                            label={t("input.labels.email")}

                            onProvide={(data) => data.email}
                            onChange={(data, value) => {
                                data.email = value;
                                if (timeoutCancel != null) {
                                    clearTimeout(timeoutCancel);
                                }
                                setTimeoutCancel(setTimeout(() => {
                                    setDistCatParams({ ...distCatParams, filterEmail: data.email, });
                                }, 1000));
                            }}
                        />
                        <EditableField<Distribution, string>
                            inputType={EditableFieldTypes.text}
                            label={t("booking.labels.phoneMobile")}
                            onProvide={(data) => data.mobile}
                            onChange={(data, value) => {
                                data.mobile = value;
                                if (timeoutCancel != null) {
                                    clearTimeout(timeoutCancel);
                                }
                                setTimeoutCancel(setTimeout(() => {
                                    setDistCatParams({ ...distCatParams, filterMobile: data.mobile, });
                                }, 1000));
                            }}
                        />

                    </Grid>
                </EditableGroup>
                <div style={{ height: isNew ? 200 : 0, }}>
                    {(((distCatParams.filterEmail != null && distCatParams.filterEmail.length > 0) || (distCatParams.filterName != null && distCatParams.filterName.length > 0)) && isNew) && (
                        <PagedDataView<IDistributionList, Distribution>
                            apiHook={api.hooks.distribution.list}
                            parameters={distCatParams}

                            renderItem={(item) => {
                                return <DistributionListItem item={item} onClick={() => {
                                    props.onExistingRecipientSelected(item);
                                }}   ></DistributionListItem>
                            }}
                        ><div style={{ padding: 20 }}>{t("editable.noExistingRecipients")}</div></PagedDataView>
                    )}
                </div>

            </DialogContent>
            <DialogActions>
                <Button color="inherit" onClick={() => {
                    props.onClose(false);
                }}>{t("general.close")}</Button>

                {isNew == false && (
                    <Fragment>
                        <Button color="inherit" onClick={() => {
                            removeRecipient(false);
                        }}>{t("general.remove")}</Button>
                        <Button color="inherit" onClick={() => {
                            deleteRecipient(false);
                        }}>{t("general.delete")}</Button>
                    </Fragment>
                )}


                <Button color="inherit" onClick={() => {
                    save();
                }}>{t("general.save")}</Button>

            </DialogActions>
        </Dialog>
    </Fragment>;
}



RecipientDialog.propTypes = {
    show: propTypes.bool.isRequired,
    category: propTypes.any.isRequired,
    selected: propTypes.any,
    onClose: propTypes.func.isRequired,
    onExistingRecipientSelected: propTypes.func.isRequired,
}

export default RecipientDialog;